import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
    validatePageInput,
    appendPageChar,
    pageErrorText,
    parsePageList,
} from "../UI/elements/pageRange";

// Модалка выбора диапазона страниц (экран 5 редизайна, #rangeModal).
// Заменяет выезжающую снизу NumberKeyboardWrapper: на киоске модалка по центру
// не перекрывается футером и не двигает раскладку страницы.
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "-", "0", ","];

function RangeModal({ value, totalPages, onApply, onCancel }) {
    const { t } = useTranslation();
    // Черновик: «Отмена» должна вернуть то, что было до открытия модалки.
    const [draft, setDraft] = useState(value || "");
    const [errorCode, setErrorCode] = useState(null);
    const [errorParams, setErrorParams] = useState(undefined);

    const setError = (code, params) => {
        setErrorCode(code);
        setErrorParams(params);
    };

    const handleKey = (key) => {
        const result = appendPageChar(draft, key);
        if (result.code) {
            setError(result.code);
            return;
        }
        setError(null);
        setDraft(result.value);
    };

    const handleErase = () => {
        setError(null);
        setDraft(draft.slice(0, -1));
    };

    const handleApply = () => {
        const result = validatePageInput(draft, totalPages);
        if (!result.valid) {
            setError(result.code, result.params);
            return;
        }
        onApply(result.cleaned);
    };

    const pagesCount = parsePageList(draft, totalPages).length;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(18,22,31,0.45)]"
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 20 }}
                transition={{ duration: 0.25 }}
                className="w-[66vw] bg-white rounded-[4vh] shadow-[0_3vh_8vh_rgba(18,22,31,0.28)] p-[3.4vh_3.6vw] flex flex-col"
            >
                <h3 className="text-center text-[3vh] font-extrabold tracking-tight">
                    {t("editDoc.range.title")}
                </h3>
                <p className="text-center text-[1.6vh] font-semibold text-[#7B8290] mt-[0.6vh]">
                    {t("editDoc.range.sub", { total: totalPages })}
                </p>

                <div className="mt-[2vh] bg-[#F1F3F7] border-[0.25vh] border-[#246CE2] rounded-[2.2vh] px-[2vw] py-[1.6vh] min-h-[6vh] flex items-center justify-center gap-[0.3vw]">
                    <span className="text-[3.2vh] font-extrabold break-all">
                        {draft}
                    </span>
                    <motion.i
                        animate={{ opacity: [1, 1, 0, 0] }}
                        transition={{
                            duration: 1,
                            repeat: Infinity,
                            times: [0, 0.49, 0.5, 1],
                        }}
                        className="block w-[0.35vh] h-[3.2vh] bg-[#246CE2] flex-none"
                    />
                </div>

                <div
                    className={`text-center text-[1.5vh] font-bold mt-[0.9vh] ${
                        errorCode ? "text-[#C2543E]" : "text-[#7B8290]"
                    }`}
                >
                    {errorCode
                        ? pageErrorText(t, errorCode, errorParams)
                        : t("editDoc.range.hint", { count: pagesCount })}
                </div>

                <div className="mt-[1.8vh] grid grid-cols-3 gap-[0.9vh]">
                    {KEYS.map((key) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => handleKey(key)}
                            className="h-[6.2vh] rounded-[2.2vh] bg-[#F1F3F7] text-[2.8vh] font-bold text-[#12161F] active:bg-[#246CE2] active:text-white transition-colors"
                        >
                            {key === "-" ? "–" : key}
                        </button>
                    ))}
                </div>

                <div className="flex gap-[0.9vh] mt-[1.8vh]">
                    <button
                        type="button"
                        onClick={handleErase}
                        className="flex-1 py-[1.6vh] rounded-full bg-[#F1F3F7] text-[1.7vh] font-bold text-[#12161F] active:bg-[#E1E4EB] transition-colors"
                    >
                        {t("editDoc.range.erase")}
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="flex-1 py-[1.6vh] rounded-full bg-[#F1F3F7] text-[1.7vh] font-bold text-[#12161F] active:bg-[#E1E4EB] transition-colors"
                    >
                        {t("editDoc.range.cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        className="flex-1 py-[1.6vh] rounded-full bg-[#246CE2] text-[1.7vh] font-bold text-white active:bg-[#1B55B4] transition-colors"
                    >
                        {t("editDoc.range.apply")}
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}

export default RangeModal;
